import React, { useCallback, useEffect, useRef, useState } from 'react';

// Right-anchored, resizable drawer (default 50% width, drag the left edge up to
// 75%). Flow: dry-run preview -> confirm -> launch background job -> poll
// progress. Used for both "approve all matching" and "reject all matching".
export default function BulkPreviewDrawer({ api, action, predicate, onClose, onDone }) {
  const [width, setWidth] = useState(50); // percent
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  const [launching, setLaunching] = useState(false);
  const draggingRef = useRef(false);
  const pollRef = useRef(null);

  const isApprove = action === 'approve';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .preview(predicate)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, predicate]);

  // Drag-to-resize the drawer width.
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const pct = ((window.innerWidth - e.clientX) / window.innerWidth) * 100;
      setWidth(Math.max(30, Math.min(75, pct)));
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const pollJob = useCallback(
    (jobId) => {
      pollRef.current = setInterval(async () => {
        try {
          const data = await api.bulkStatus(jobId);
          setJob(data);
          if (data.status === 'done' || data.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (data.status === 'done' && typeof onDone === 'function') onDone(data);
          }
        } catch (_e) {
          // transient; keep polling
        }
      }, 1500);
    },
    [api, onDone]
  );

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const launch = useCallback(async () => {
    if (!preview) return;
    setLaunching(true);
    setError('');
    try {
      const resp = await api.startBulk(action, predicate, preview.matched_entries);
      setJob({ status: 'queued', total: preview.matched_entries, processed: 0, approved: 0, rejected: 0 });
      pollJob(resp.job_id);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLaunching(false);
    }
  }, [api, action, predicate, preview, pollJob]);

  const progressPct =
    job && job.total ? Math.round((Number(job.processed || 0) / Number(job.total)) * 100) : 0;
  const running = job && (job.status === 'queued' || job.status === 'running');
  const finished = job && (job.status === 'done' || job.status === 'failed');

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div
        className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-flex flex-column"
        style={{ width: `${width}%`, minWidth: 360 }}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        {/* resize handle */}
        <div
          onMouseDown={() => {
            draggingRef.current = true;
            document.body.style.userSelect = 'none';
          }}
          title="Drag to resize"
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize', background: 'transparent' }}
        />
        <div className={`p-3 border-bottom d-flex justify-content-between align-items-center ${isApprove ? 'bg-primary-subtle' : 'bg-danger-subtle'}`}>
          <h5 className="mb-0">{isApprove ? 'Approve all matching' : 'Reject all matching'}</h5>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
        </div>

        <div className="p-3 flex-grow-1 overflow-auto">
          {error && <div className="alert alert-danger">{error}</div>}

          {loading && <div className="text-muted py-4 text-center">Computing dry-run…</div>}

          {!loading && preview && !job && (
            <>
              <div className="alert alert-info">
                This will <strong>{isApprove ? 'approve' : 'reject'}</strong>{' '}
                <strong>{preview.matched_entries}</strong> pending matches across{' '}
                <strong>{preview.matched_rows}</strong> AHRI rows.
              </div>
              <div className="small text-muted mb-2">Filter: <code>{preview.predicate_description}</code></div>

              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <h6 className="small text-uppercase text-muted">By field</h6>
                  <ul className="list-group list-group-flush">
                    {(preview.by_field || []).map((b) => (
                      <li key={b.field} className="list-group-item d-flex justify-content-between py-1 px-0">
                        <span>{b.field}</span><span className="badge text-bg-secondary">{b.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-12 col-lg-6">
                  <h6 className="small text-uppercase text-muted">By brand (AHRI → candidate)</h6>
                  <ul className="list-group list-group-flush">
                    {(preview.by_brand_pair || []).slice(0, 12).map((b) => (
                      <li key={b.pair} className="list-group-item d-flex justify-content-between py-1 px-0">
                        <span className="small">{b.pair}</span><span className="badge text-bg-secondary">{b.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <h6 className="small text-uppercase text-muted mt-3">Sample (first 20)</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr><th>AHRI</th><th>Field</th><th>Source</th><th>Candidate</th><th>Score</th></tr>
                  </thead>
                  <tbody>
                    {(preview.sample || []).map((s, i) => (
                      <tr key={`${s.ahri_id}-${i}`}>
                        <td className="small">{s.ahri_id}</td>
                        <td className="small">{s.field}</td>
                        <td className="small"><code>{s.source_value}</code></td>
                        <td className="small">{(s.candidate || {}).product_code} <span className="text-muted">{(s.candidate || {}).manufacturer_name}</span></td>
                        <td className="small">{Number(s.score || 0).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {job && (
            <div className="py-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="fw-semibold text-capitalize">{job.status}</span>
                <span className="small text-muted">{job.processed || 0} / {job.total || 0}</span>
              </div>
              <div className="progress mb-3" style={{ height: 20 }}>
                <div
                  className={`progress-bar ${job.status === 'failed' ? 'bg-danger' : isApprove ? '' : 'bg-warning'} ${running ? 'progress-bar-striped progress-bar-animated' : ''}`}
                  style={{ width: `${job.status === 'failed' ? 100 : progressPct}%` }}
                >
                  {job.status === 'failed' ? 'Failed' : `${progressPct}%`}
                </div>
              </div>
              <div className="d-flex gap-3 small">
                <span className="text-success">Approved: {job.approved || 0}</span>
                <span className="text-warning">Rejected: {job.rejected || 0}</span>
                <span className="text-danger">Errors: {(job.errors || []).length}</span>
              </div>
              {job.message && <div className="small text-muted mt-2">{job.message}</div>}
              {(job.errors || []).length > 0 && (
                <details className="mt-2">
                  <summary className="small text-danger" style={{ cursor: 'pointer' }}>
                    {(job.errors || []).length} errors
                  </summary>
                  <ul className="small mb-0 mt-1">
                    {(job.errors || []).slice(0, 50).map((e, i) => (
                      <li key={i}>{e.ahri_id}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="p-3 border-top d-flex justify-content-end gap-2">
          {!finished && (
            <button type="button" className="btn btn-light" onClick={onClose} disabled={launching}>
              {running ? 'Run in background' : 'Cancel'}
            </button>
          )}
          {finished && (
            <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
          )}
          {!job && (
            <button
              type="button"
              className={`btn ${isApprove ? 'btn-primary' : 'btn-danger'}`}
              onClick={launch}
              disabled={loading || launching || !preview || (preview && preview.matched_entries === 0)}
            >
              {launching ? 'Starting…' : `${isApprove ? 'Approve' : 'Reject'} ${preview ? preview.matched_entries : ''} matches`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
